import React, { useState } from "react";
import { UserRound, ChevronUp, MessageCircle, Download, Lightbulb, Pencil, Check, X, Volume2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fire = (name) => window.dispatchEvent(new Event(name));

export const EditablePhone = ({ userPhone, small = false }) => {
  const { setUserData } = useAuth();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(userPhone || "+");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const digits = val.replace(/\D/g, "");
    if (digits.length < 8) return toast.error("Enter a valid number with country code, e.g. +16513472708");
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { phone: `+${digits}` });
      setUserData(data);
      setEditing(false);
      toast.success(`Got it — we'll call you on ${data.phone}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="flex items-center gap-1.5">
        <input
          autoFocus
          type="tel"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="+16513472708"
          className={`min-w-0 flex-1 bg-white border border-blue-300 rounded-lg px-2 py-1 font-mono-dial font-bold text-zinc-900 outline-none focus:border-primary ${small ? "text-sm" : ""}`}
          data-testid="edit-phone-input"
        />
        <button onClick={save} disabled={saving} className="h-7 w-7 shrink-0 rounded-full bg-emerald-500 text-white flex items-center justify-center active:scale-95" data-testid="edit-phone-save" aria-label="Save">
          <Check className="h-4 w-4" />
        </button>
        <button onClick={() => { setEditing(false); setVal(userPhone || "+"); }} className="h-7 w-7 shrink-0 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center active:scale-95" data-testid="edit-phone-cancel" aria-label="Cancel">
          <X className="h-4 w-4" />
        </button>
      </span>
    );
  }
  return (
    <button onClick={() => { setVal(userPhone || "+"); setEditing(true); }} className="flex items-center gap-1.5 group min-w-0 w-full" data-testid="edit-phone-trigger">
      {userPhone ? (
        <span className={`font-mono-dial font-bold text-zinc-900 truncate min-w-0 flex-1 text-left ${small ? "text-sm" : ""}`} data-testid={small ? "info-user-phone-mobile" : "info-user-phone"}>{userPhone}</span>
      ) : (
        <span className={`font-bold text-primary ${small ? "text-xs" : "text-sm"}`}>Tap to add your number</span>
      )}
      <Pencil className="h-3.5 w-3.5 shrink-0 text-zinc-400 group-hover:text-primary transition-colors" />
    </button>
  );
};

const ZaniCircle = ({ size }) => (
  <span className={`ai-glow-btn ${size} shrink-0 rounded-full text-white flex items-center justify-center`}>
    <MessageCircle style={{ height: 18, width: 18 }} />
  </span>
);

const AppCircle = ({ size }) => (
  <span className={`relative ${size} shrink-0 rounded-full bg-zinc-900 flex items-center justify-center shadow-md`}>
    <img src="/icons/icon-192.png" alt="" className="h-7 w-7 rounded-full" />
    <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary flex items-center justify-center border-2 border-white">
      <Download className="h-2 w-2 text-white" />
    </span>
  </span>
);

const FeedbackCircle = ({ size }) => (
  <span className={`feedback-blink ${size} shrink-0 rounded-full bg-[#E11D48] text-white flex items-center justify-center`}>
    <Lightbulb style={{ height: 18, width: 18 }} />
  </span>
);

const RAIL_ACTIONS = [
  { Circle: ZaniCircle, event: "zani-open", key: "zani", labelShort: "Zeep assistant", labelLong: "Zeep assistant" },
  { Circle: AppCircle, event: "zanitel-install-request", key: "app", labelShort: "App", labelLong: "Get the app" },
  { Circle: FeedbackCircle, event: "zanitel-feedback-open", key: "feedback", labelShort: "Feedback", labelLong: "Help us get better — share your view" },
];

const STATUS_META = {
  idle: { dot: "bg-zinc-300", ring: "border-zinc-200 bg-white", text: "Line idle", textColor: "text-zinc-400" },
  connecting: { dot: "red-blink bg-red-500", ring: "border-red-200 bg-red-50/60", text: "Connecting…", textColor: "text-red-500" },
  connected: { dot: "red-blink bg-emerald-500", ring: "border-emerald-300 bg-emerald-50", text: "Connected", textColor: "text-emerald-600" },
};

export const speakerCheck = async () => {
  try { const a = new Audio("/sounds/05_zeeptel_success.wav"); a.volume = 0.6; await a.play(); } catch { toast.error("Speakers blocked — check your sound settings"); return; }
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    toast.success("You're all set — speakers playing ✓ microphone ready ✓");
  } catch {
    toast.error("Speakers work, but the microphone is blocked — click the 🔒 icon in your address bar and allow the mic");
  }
};

export const QuickActions = () => (
  <div className="grid grid-cols-4 gap-2" data-testid="quick-actions">
    {RAIL_ACTIONS.map((a) => (
      <button key={a.key} type="button" onClick={() => fire(a.event)} data-testid={`quick-${a.key}-button`}
        className="flex flex-col items-center gap-1.5 rounded-2xl bg-white border border-black/10 hover:border-primary/40 py-3 px-1 active:scale-95 transition">
        <a.Circle size="h-10 w-10" />
        <span className="text-[10px] font-semibold text-zinc-600 text-center leading-tight">{a.labelShort}</span>
      </button>
    ))}
    <button type="button" onClick={speakerCheck} data-testid="quick-speaker-check-button"
      className="flex flex-col items-center gap-1.5 rounded-2xl bg-white border border-black/10 hover:border-primary/40 py-3 px-1 active:scale-95 transition">
      <span className="h-10 w-10 shrink-0 rounded-full border-2 border-zinc-200 bg-white flex items-center justify-center">
        <Volume2 className="h-4 w-4 text-zinc-400" />
      </span>
      <span className="text-[10px] font-semibold text-zinc-600 text-center leading-tight">Test sound</span>
    </button>
  </div>
);

export const RailActions = ({ mobile = false, status = "idle" }) => {
  const s = STATUS_META[status] || STATUS_META.idle;
  return (
    <>
      {RAIL_ACTIONS.map((a) => (
        <button
          key={a.key}
          onClick={() => fire(a.event)}
          className={mobile ? "flex items-center gap-2 active:scale-95 transition-transform" : "flex items-center gap-3 group w-full"}
          data-testid={`panel-${a.key}-circle${mobile ? "-mobile" : ""}`}
        >
          <a.Circle size={mobile ? "h-10 w-10" : "h-11 w-11"} />
          <span className={mobile ? "text-[10px] font-semibold text-zinc-500 text-left leading-tight" : "text-sm font-semibold text-zinc-600 group-hover:text-zinc-900 transition-colors text-left leading-tight"}>
            {mobile ? a.labelShort : a.labelLong}
          </span>
        </button>
      ))}
      <button
        onClick={speakerCheck}
        className={mobile ? "flex items-center gap-2 active:scale-95 transition-transform" : "flex items-center gap-3 group w-full"}
        data-testid={`panel-speaker-check${mobile ? "-mobile" : ""}`}
      >
        <span className={`${mobile ? "h-10 w-10" : "h-11 w-11"} shrink-0 rounded-full border-2 border-zinc-200 bg-white flex items-center justify-center group-hover:border-primary/40 transition-colors`}>
          <Volume2 className="h-4 w-4 text-zinc-400" />
        </span>
        <span className={mobile ? "text-[10px] font-semibold text-zinc-500 text-left leading-tight" : "text-sm font-semibold text-zinc-600 group-hover:text-zinc-900 transition-colors text-left leading-tight"}>
          {mobile ? "Test sound" : "Test speakers & mic"}
        </span>
      </button>
      <div className={mobile ? "flex items-center gap-2" : "flex items-center gap-3 w-full"} data-testid={`connection-status-circle${mobile ? "-mobile" : ""}`}>
        <span className={`${mobile ? "h-10 w-10" : "h-11 w-11"} shrink-0 rounded-full border-2 flex items-center justify-center ${s.ring}`}>
          <span className={`h-3.5 w-3.5 rounded-full ${s.dot}`} data-testid="rail-connection-blinker" />
        </span>
        <span className={`${mobile ? "text-[10px]" : "text-sm"} font-semibold text-left leading-tight ${s.textColor}`}>{s.text}</span>
      </div>
    </>
  );
};

export const RailActionsCard = () => (
  <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm space-y-4" data-testid="rail-actions-card">
    <RailActions />
  </div>
);

export const CallInfoPanel = ({ userPhone, dialedNumber, contactName, lastDialed, compact = false, callMode, hideActions = false }) => {
  if (compact) {
    return (
      <div className="lg:hidden bg-white border border-black/10 rounded-2xl px-2.5 py-3 shadow-sm self-start" data-testid="call-info-strip">
        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Receiver</div>
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 px-2.5 py-1.5 min-w-0" data-testid="info-receiver-box-mobile">
          {dialedNumber ? (
            <span className="font-mono-dial text-xs font-bold text-zinc-900 break-all block" data-testid="info-dialed-number-mobile">
              {contactName ? `${contactName} · ` : ""}{dialedNumber}
            </span>
          ) : lastDialed ? (
            <span className="text-[10px] text-zinc-500 block leading-tight">last: <b className="font-mono text-zinc-700 break-all">{lastDialed}</b></span>
          ) : (
            <span className="text-zinc-300 text-sm">—</span>
          )}
        </div>
        <div className="flex items-center justify-center gap-0.5 py-1" aria-hidden="true">
          <span className="text-[8px] font-bold uppercase tracking-widest text-zinc-300 mr-0.5">Connect</span>
          {[0, 1].map((i) => (
            <ChevronUp key={i} className="arrow-up-anim h-3.5 w-3.5 text-primary" style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
        </div>
        <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/60 px-2.5 py-1.5 min-w-0" data-testid="info-your-box-mobile">
          <EditablePhone userPhone={userPhone} small />
        </div>
        <div className="flex flex-col gap-3 mt-3 pt-3 border-t border-black/5">
          <RailActions mobile />
        </div>
      </div>
    );
  }
  return (
    <aside className="hidden lg:block" data-testid="call-info-panel">
      <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm">
        {/* A1 — Receiver */}
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Receiver</div>
        <div className="rounded-full border-2 border-emerald-300 bg-emerald-50/60 px-4 py-2.5" data-testid="info-receiver-box">
          {contactName && (
            <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-900" data-testid="info-contact-name">
              <UserRound className="h-3.5 w-3.5 text-emerald-500" /> {contactName}
            </div>
          )}
          <div className="font-mono-dial font-bold text-zinc-900" data-testid="info-dialed-number">
            {dialedNumber || <span className="text-zinc-300">—</span>}
          </div>
          {!dialedNumber && lastDialed && (
            <div className="text-[11px] text-zinc-400">last: <span className="font-mono font-semibold text-zinc-600">{lastDialed}</span> · press Call to redial</div>
          )}
        </div>

        {/* Connect arrows */}
        <div className="flex items-center justify-center gap-1 py-1.5" aria-hidden="true">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-300 mr-1">Connect</span>
          {[0, 1].map((i) => (
            <ChevronUp key={i} className="arrow-up-anim h-4 w-4 text-primary" style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
        </div>

        {/* A2 — Your number (read-only dashboard; edit lives in the connect panel) */}
        <div
          className={`rounded-full border-2 px-4 py-2.5 transition-colors ${callMode === "callback" ? "border-emerald-400 bg-emerald-50/70 green-border-blink" : "border-blue-200 bg-blue-50/60"}`}
          data-testid="info-your-box"
        >
          {userPhone ? (
            <span className="font-mono-dial font-bold text-zinc-900 truncate block" data-testid="info-user-phone">{userPhone}</span>
          ) : (
            <span className="text-sm font-bold text-primary">Add your number in the panel →</span>
          )}
          <div className={`text-[11px] ${callMode === "callback" ? "text-emerald-600 font-semibold" : "text-zinc-400"}`}>
            {callMode === "callback" ? "We ring this phone, then connect you" : "Your phone — edit it in the connect panel"}
          </div>
        </div>
      </div>

      {!hideActions && (
        <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm mt-4 space-y-4">
          <RailActions />
        </div>
      )}
    </aside>
  );
};
